import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { API_CONFIG, buildApiUrl } from '../config/api';

interface Station {
  id: number;
  name: string;
  code: string;
  line_name?: string;
  line_id?: number;
}

interface PricingMatrixItem {
  from_station_id: number;
  from_station_name: string;
  from_station_code: string;
  to_station_id: number;
  to_station_name: string;
  to_station_code: string;
  price: number | null;
  station_count: number | null;
  distance_km: number | null;
}

interface PricingMatrixData {
  company_id: number;
  company_name: string;
  stations: Station[];
  pricing_data: PricingMatrixItem[];
}

interface Company {
  id: number;
  name: string;
  code: string;
  station_count: number;
}

const PricingMatrix: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [matrixData, setMatrixData] = useState<PricingMatrixData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.PRICING_COMPANIES));
      const data = await response.json();
      setCompanies(data.companies);
    } catch (err) {
      setError('Failed to fetch companies');
      console.error('Error fetching companies:', err);
    }
  };

  const fetchPricingMatrix = async (companyId: number) => {
    setLoading(true);
    setError('');

    try {
      // Fetch pricing matrix (already includes stations with line_name)
      const matrixResponse = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.PRICING_MATRIX(companyId)));
      const matrixData = await matrixResponse.json();

      // Sort stations by line name, then by code
      const sortedStations = [...matrixData.stations].sort((a: any, b: any) => {
        const lineCompare = (a.line_name || '').localeCompare(b.line_name || '');
        if (lineCompare !== 0) return lineCompare;
        return a.code.localeCompare(b.code);
      });

      // Update matrix data with sorted stations
      setMatrixData({
        ...matrixData,
        stations: sortedStations
      });
    } catch (err) {
      setError('Failed to fetch pricing matrix');
      console.error('Error fetching pricing matrix:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (companyId: number) => {
    setSelectedCompany(companyId);
    fetchPricingMatrix(companyId);
  };

  const getPriceForStations = (fromStationId: number, toStationId: number): number | null => {
    if (!matrixData) return null;

    const pricingItem = matrixData.pricing_data.find(
      item => item.from_station_id === fromStationId && item.to_station_id === toStationId
    );

    return pricingItem ? pricingItem.price : null;
  };

  const getStationCountForStations = (fromStationId: number, toStationId: number): number | null => {
    if (!matrixData) return null;

    const pricingItem = matrixData.pricing_data.find(
      item => item.from_station_id === fromStationId && item.to_station_id === toStationId
    );

    return pricingItem ? pricingItem.station_count : null;
  };

  const exportToExcel = () => {
    if (!matrixData) {
      setError('No data to export');
      return;
    }

    // Create Excel workbook
    const wb = XLSX.utils.book_new();

    // Create matrix data for Excel
    const excelData: any[][] = [];

    // Helper function to format station display
    const formatStationDisplay = (station: Station) => `${station.code}(${station.name})`;

    // Header row
    const header = ['From Station', ...matrixData.stations.map(formatStationDisplay)];
    excelData.push(header);

    // Data rows
    matrixData.stations.forEach(fromStation => {
      const row = [formatStationDisplay(fromStation)];
      matrixData.stations.forEach(toStation => {
        if (fromStation.id === toStation.id) {
          row.push('—');
        } else {
          const price = getPriceForStations(fromStation.id, toStation.id);
          row.push(price?.toString() || '');
        }
      });
      excelData.push(row);
    });

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Add metadata sheet
    const metadataData = [
      ['Company ID', matrixData.company_id],
      ['Company Name', matrixData.company_name],
      ['Export Date', new Date().toISOString()],
      ['Total Stations', matrixData.stations.length],
      ['Total Routes', matrixData.pricing_data.length],
      ['', ''],
      ['Instructions:', ''],
      ['1. To import, modify prices in the matrix above', ''],
      ['2. Keep station codes unchanged', ''],
      ['3. Use numbers for prices, empty cells for no route', ''],
      ['4. Upload this file back to import changes', '']
    ];
    const metaWs = XLSX.utils.aoa_to_sheet(metadataData);

    // Add detailed data sheet
    const detailedData = [
      ['From Station Code', 'From Station Name', 'To Station Code', 'To Station Name', 'Price', 'Station Count', 'Distance (km)']
    ];
    matrixData.pricing_data.forEach(item => {
      detailedData.push([
        item.from_station_code,
        item.from_station_name,
        item.to_station_code,
        item.to_station_name,
        item.price?.toString() || '',
        item.station_count?.toString() || '',
        item.distance_km?.toString() || ''
      ]);
    });
    const detailedWs = XLSX.utils.aoa_to_sheet(detailedData);

    // Add worksheets to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Pricing Matrix');
    XLSX.utils.book_append_sheet(wb, metaWs, 'Metadata');
    XLSX.utils.book_append_sheet(wb, detailedWs, 'Detailed Data');

    // Save file
    const fileName = `${matrixData.company_name}_Pricing_Matrix_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);

    setSuccess('Excel file exported successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedCompany) {
      setError('Please select a company first');
      return;
    }

    setImporting(true);
    setError('');

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        // Read the pricing matrix sheet
        const sheetName = 'Pricing Matrix';
        if (!workbook.SheetNames.includes(sheetName)) {
          throw new Error('Excel file must contain a "Pricing Matrix" sheet');
        }

        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        if (jsonData.length < 2) {
          throw new Error('Excel file must contain header and data rows');
        }

        const headerRow = jsonData[0];
        const stationDisplays = headerRow.slice(1); // Remove first column "From Station"

        // Helper function to extract station code from "CODE(Station Name)" format
        const extractStationCode = (display: string): string => {
          if (typeof display !== 'string') return display;
          const match = display.match(/^([^(]+)/);
          return match ? match[1].trim() : display;
        };

        // Process import data
        const importData: any[] = [];

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          const fromStationDisplay = row[0];
          const fromStationCode = extractStationCode(fromStationDisplay);

          // Find from station
          const fromStation = matrixData?.stations.find(s => s.code === fromStationCode);
          if (!fromStation) continue;

          for (let j = 1; j < row.length && j <= stationDisplays.length; j++) {
            const toStationDisplay = stationDisplays[j - 1];
            const toStationCode = extractStationCode(toStationDisplay);
            const price = row[j];

            // Skip empty cells or same station
            if (!price || price === '—' || fromStationCode === toStationCode) continue;

            // Find to station
            const toStation = matrixData?.stations.find(s => s.code === toStationCode);
            if (!toStation) continue;

            // Parse price
            const numericPrice = parseFloat(price.toString());
            if (isNaN(numericPrice)) continue;

            importData.push({
              company_id: selectedCompany,
              from_station_id: fromStation.id,
              to_station_id: toStation.id,
              price: numericPrice,
              currency: 'THB',
              is_active: true
            });
          }
        }

        // Send bulk import to backend
        if (importData.length > 0) {
          const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.PRICING_BULK_IMPORT), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              company_id: selectedCompany,
              pricing_data: importData
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Import failed');
          }

          const result = await response.json();
          setSuccess(`Import completed: ${result.created || 0} created, ${result.updated || 0} updated`);

          // Refresh matrix data
          fetchPricingMatrix(selectedCompany);
        } else {
          setError('No valid pricing data found in Excel file');
        }

      } catch (error: any) {
        setError(`Import failed: ${error.message}`);
      } finally {
        setImporting(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const triggerFileImport = () => {
    if (!selectedCompany) {
      setError('Please select a company first');
      return;
    }
    fileInputRef.current?.click();
  };

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white shadow-lg rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Company Pricing Matrix</h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {success}
          </div>
        )}

        {/* Company Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Company
          </label>
          <select
            value={selectedCompany || ''}
            onChange={(e) => handleCompanyChange(Number(e.target.value))}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a company...</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name} ({company.code}) - {company.station_count} stations
              </option>
            ))}
          </select>
        </div>

        {/* Excel Export/Import Buttons */}
        {selectedCompany && (
          <div className="mb-6 flex flex-wrap gap-3">
            <button
              onClick={exportToExcel}
              disabled={!matrixData || loading}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export to Excel
            </button>

            <button
              onClick={triggerFileImport}
              disabled={importing || loading}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {importing ? 'Importing...' : 'Import from Excel'}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileImport}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading pricing matrix...</p>
          </div>
        )}

        {matrixData && !loading && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {matrixData.company_name} - Station-to-Station Pricing Matrix
              </h3>
              <p className="text-gray-600">
                {matrixData.stations.length} stations • Prices in THB
              </p>
            </div>

            {/* Pricing Matrix Table */}
            <div className="overflow-auto max-h-96">
              <table className="min-w-full border-collapse border border-gray-300">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="border border-gray-300 p-2 text-sm font-medium text-gray-700 min-w-[120px]">
                      From \\ To
                    </th>
                    {matrixData.stations.map((station) => (
                      <th
                        key={station.id}
                        className="border border-gray-300 p-2 text-sm font-medium text-gray-700 min-w-[120px] transform -rotate-45 h-20"
                        title={`${station.code} - ${station.name}`}
                      >
                        <div className="transform rotate-45 whitespace-nowrap">
                          {station.code}({station.name})
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixData.stations.map((fromStation) => (
                    <tr key={fromStation.id} className="hover:bg-gray-50">
                      <td className="border border-gray-300 p-2 font-medium text-sm bg-gray-100 sticky left-0">
                        <div title={fromStation.name}>
                          {fromStation.code}({fromStation.name})
                        </div>
                      </td>
                      {matrixData.stations.map((toStation) => (
                        <td
                          key={toStation.id}
                          className={`border border-gray-300 p-1 text-center text-sm ${
                            fromStation.id === toStation.id
                              ? 'bg-gray-200 text-gray-500'
                              : 'hover:bg-blue-50'
                          }`}
                        >
                          {fromStation.id === toStation.id ? (
                            <span className="text-gray-400">—</span>
                          ) : (
                            <div className="space-y-1">
                              <div className="font-medium text-green-600">
                                ฿{getPriceForStations(fromStation.id, toStation.id) || '—'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {getStationCountForStations(fromStation.id, toStation.id) || '—'} stn
                              </div>
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!selectedCompany && !loading && (
          <div className="text-center py-12 text-gray-500">
            <p>Please select a company to view the pricing matrix</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingMatrix;